import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axios from "axios";
import { Duck, ApiDuck } from "@/interfaces/duck";
import { Constant } from "@/config/constants";
import { useCruiseStore } from "@/stores/cruiseStore";

export const useDuckStore = defineStore('ducks', () => {
    const ducks = ref<Array<Duck>>([]);
    const currentDuck = ref<Duck>();

    const getDuckById = computed(() => (guid: string) => {
        return ducks.value.find(d => d.uuid === guid);
    })

    const getTotalDucks = computed(() => {
        return ducks.value.length;
    })
    const getFoundDucks = computed(() => {
        return ducks.value.filter(d => d.found > 0).length;
    })

    const getRemainingDucks = computed(() => {
        return getTotalDucks.value - getFoundDucks.value;
    })

    const getDucksOrderedById = computed(() => {
        return ducks.value.sort((a, b) => {
            if (a.found !== b.found) {
                return a.found ? -1 : 1
            }

            return a.id - b.id
        })
    })

    const createDucks = async (guid: string) => {
        console.log('Creating ducks with UUID:', guid); // Debug log
        try {
            await getDucks(guid);
            await setCurrentDuck(guid);
        } catch (err) {
            console.error('Error in createDucks:', err); // Debug log
            throw err;
        }
    }

    const getDucks = (guid: string) => {
        const cruiseStore = useCruiseStore();

        return new Promise<void>((resolve, reject) => {
            axios.get(`${Constant.API_URL}/get-cruise-info-from-duck/${guid}`)
            .then(async res => {
                if(res && res.status === 200) {
                    cruiseStore.assignCurrentCruise({
                        id: 1,
                        ...res.data
                    });
    
                    ducks.value.length = 0;
                    
                    res.data.duck.forEach((duck: ApiDuck) => {
                        // No need to modify the image as it comes with the complete data URI
                        ducks.value.push({
                            uuid: undefined,
                            idCruise: 1,
                            ...duck
                        })
                    });

                    resolve();
                } else {
                    reject("Cannot retrieve ducks");
                }
            })
            .catch(err => {
                reject(err);
            })
        })
    }

    const setCurrentDuck = (guid: string) => {
        return new Promise<void>((resolve, reject) => {
            // Find the duck in our local array and set it as current
            const duck = ducks.value.find(d => String(d.id) === guid);
            if (duck) {
                currentDuck.value = duck;
                currentDuck.value.uuid = guid;
                resolve();
            } else {
                reject("Duck not found");
            }
        });
    }

    const markDuckAsFound = (guid: string) => {
        const duck = ducks.value.find(d => d.uuid === guid);
        if (duck) {
            duck.found = 1; // Set found status to 1 (found)
        }
        
        // If it's the current duck, also update it
        if (currentDuck.value && currentDuck.value.uuid === guid) {
            currentDuck.value.found = 1;
        }
        
        // Make sure API also has this information
        axios.get(`${Constant.API_URL}/duck-found/${guid}`)
            .catch(error => console.error('Error marking duck as found in API:', error));
    }

    const sendMessage = (message: string, guid: string) => {
        return axios.post(Constant.API_URL + Constant.SEND_MESSAGE, { duckUUID: guid, message: message })
    }

    const hideDuck = (guid: string) => {
        // Update the local duck status to hidden (found = 0) when hiding a duck
        const duck = ducks.value.find(d => d.uuid === guid);
        if (duck) {
            duck.found = 0; // Set found status to 0 (hidden)
        }
        
        // If it's the current duck, also update it
        if (currentDuck.value && currentDuck.value.uuid === guid) {
            currentDuck.value.found = 0;
        }
        
        // Make the API call to hide the duck
        return axios.post(Constant.API_URL + Constant.HIDE_DUCK, { duckUUID: guid })
    }

    // Claim a found duck using its UUID (requires authentication)
    const claimDuck = async (duckUuid: string) => {
        const url = `${Constant.API_URL}${Constant.CLAIM_FOUND_DUCK}/${duckUuid}`;
        const response = await axios.post(url);
        // On success, reflect the claim in local state if applicable
        if (response.status === 200) {
            const duck = ducks.value.find(d => d.uuid === duckUuid);
            if (duck) {
                // Mark as found/claimed locally
                duck.found = 1;
            }
            if (currentDuck.value?.uuid === duckUuid) {
                currentDuck.value.found = 1;
            }
        }
        return response;
    }

    return { 
        ducks,
        currentDuck,
        getDuckById, 
        getTotalDucks,
        getFoundDucks,
        getRemainingDucks,
        getDucksOrderedById,
        createDucks,
        getDucks,
        sendMessage,
        hideDuck,
        markDuckAsFound,
        claimDuck
    }
});